// src/pages/post/Feed.jsx
import React, { useEffect, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/userContext';
import usePosts from '../../hooks/usePosts';
import PostItem from '../../components/post/PostItem';
import Loader from '../../components/common/Loader';
import Tabs from '../../components/ui/Tabs';
import Tab from '../../components/ui/Tab';

const Feed = () => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('following');
  
  const {
    posts,
    loading,
    error,
    pagination,
    fetchFeed,
    fetchPosts,
    fetchTrendingPosts
  } = usePosts();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Initial data loading based on active tab
  useEffect(() => {
    if (!currentUser) return;
    
    loadTabContent(activeTab);
  }, [activeTab, currentUser]);

  // Load content based on selected tab
  const loadTabContent = (tab) => {
    switch (tab) {
      case 'following':
        fetchFeed();
        break;
      case 'explore':
        fetchPosts();
        break;
      case 'trending':
        fetchTrendingPosts();
        break;
      default:
        fetchFeed();
    }
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // Handle pagination
  const handleLoadMore = () => {
    const nextPage = pagination.page + 1;
    
    switch (activeTab) {
      case 'following':
        fetchFeed(nextPage);
        break;
      case 'explore':
        fetchPosts(nextPage);
        break;
      case 'trending':
        fetchTrendingPosts(nextPage);
        break;
      default:
        fetchFeed(nextPage);
    }
  };

  if (!currentUser) {
    return null; // Or a loading state while redirecting
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="min-h-[300px] flex items-center justify-center">
          <Loader />
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      );
    }
    
    if (!posts || posts.length === 0) {
      let emptyStateContent;
      
      switch (activeTab) {
        case 'following':
          emptyStateContent = {
            title: 'No posts yet',
            message: 'Follow more users to see their posts here, or explore new content.',
            action: (
              <Link to="/authors" className="px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors">
                Find Users to Follow
              </Link>
            )
          };
          break;
        case 'explore':
          emptyStateContent = {
            title: 'No posts found',
            message: 'Be the first to create content!',
            action: (
              <Link to="/create" className="px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors">
                Create Post
              </Link>
            )
          };
          break;
        case 'trending':
          emptyStateContent = {
            title: 'No trending posts yet',
            message: 'Check back later for popular content.',
            action: null
          };
          break;
        default:
          emptyStateContent = {
            title: 'No content available',
            message: 'Try a different tab or check back later.',
            action: null
          };
      }
      
      return (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-2">{emptyStateContent.title}</h3>
          <p className="text-gray-600 mb-6">{emptyStateContent.message}</p>
          {emptyStateContent.action && (
            <div className="flex justify-center">
              {emptyStateContent.action}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {Array.isArray(posts) && posts.map(post => (
          <PostItem
            key={post._id}
            postID={post._id}
            thumbnail={post.thumbnail}
            category={post.category}
            title={post.title}
            description={post.description}
            authorID={post.creator._id || post.creator}
            createdAt={post.createdAt}
            likes={post.likes}
            comments={post.comments}
            liked={post.liked}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Feed</h1>
        
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button 
                onClick={() => handleTabChange('following')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'following' 
                    ? 'text-primary border-b-2 border-primary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Following
              </button>
              <button 
                onClick={() => handleTabChange('explore')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'explore' 
                    ? 'text-primary border-b-2 border-primary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Explore
              </button>
              <button 
                onClick={() => handleTabChange('trending')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'trending' 
                    ? 'text-primary border-b-2 border-primary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Trending
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {renderContent()}
          </div>
        </div>
        
        {!loading && !error && posts && posts.length > 0 && pagination && pagination.page < pagination.pages && (
          <div className="flex justify-center mt-8">
            <button 
              className="px-6 py-3 bg-white border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Feed;